// Academic Search Provider types
export type AcademicSearchProvider =
  | "pubmed"
  | "semantic-scholar"
  | "google-scholar"
  | "arxiv"
  | "eric";

export const AcademicSearchProviderValues: readonly AcademicSearchProvider[] = [
  "pubmed",
  "semantic-scholar",
  "google-scholar",
  "arxiv",
  "eric",
] as const;

// Academic Search Author interface
export interface AcademicSearchAuthor {
  name: string;
  url?: string;
}

// Academic Search Base publication interface
export interface AcademicSearchBasePublication {
  doi?: string;
  title?: string;
  url?: string;
  providerURL?: string;
  authors?: AcademicSearchAuthor[];
  date?: string;
  year?: number;
  totalCitations?: number;
  totalReferences?: number;
  abstract?: string;
  pdfURL?: string;
  provider?: AcademicSearchProvider;
}

// Academic Search Semantic Scholar provider data interface
export interface AcademicSearchSemanticScholarData {
  paperId: string;
  venue?: string;
  publicationTypes?: string[];
  influentialCitationCount?: number;
  fieldsOfStudy?: string[];
  s2FieldsOfStudy?: {
    category: string;
    source: string;
  }[];
  openAccessPdfStatus?:
    | "GREEN"
    | "GOLD"
    | "HYBRID"
    | "BRONZE"
    | "CLOSED"
    | null;
  externalIds?: {
    CorpusId?: number;
    PubMed?: string;
    ArXiv?: string;
    MAG?: string;
    ACL?: string;
    PubMedCentral?: string;
    DBLP?: string;
  };
  isOpenAccess?: boolean;
  citationVelocity?: number;
  publicationVenue?: {
    id?: string;
    name?: string;
    type?: string;
    url?: string;
    alternate_names?: string[];
    issn?: string;
  };
}

// Academic Search Google Scholar provider data interface
export interface AcademicSearchGoogleScholarData {
  scholarId: string;
  displayedLink: string;
  snippet?: string;
  citedByLink?: string;
  citedByCount?: string;
  versionsLink?: string;
  versionsCount?: string;
  relatedPagesLink?: string;
  resources?: {
    title: string;
    type: string;
    link: string;
  }[];
}

// Academic Search PubMed provider data interface
export interface AcademicSearchPubMedData {
  pmid: string;
  pmcid?: string;
  journal?: string;
  journalAbbreviation?: string;
  volume?: string;
  issue?: string;
  pages?: string;
  meshTerms?: string[];
  keywords?: string[];
  publicationTypes?: string[];
  authorAffiliations?: {
    author: string;
    affiliation: string;
  }[];
  publicationStatus?: string;
  language?: string;
  grantNumbers?: string[];
  conflictOfInterest?: string;
  chemicalList?: string[];
  dataAvailability?: string;
  history?: {
    received?: string;
    accepted?: string;
    revised?: string;
  };
}

// Academic Search ArXiv provider data interface
export interface AcademicSearchArxivData {
  arxivId: string;
  version?: string;
  categories?: string[];
  comment?: string;
  journalRef?: string;
  primaryCategory?: string;
  submittedDate?: string;
  updatedDate?: string;
  license?: string;
  announceType?: "new" | "replace" | "cross-list";
}

// Academic Search ERIC provider data interface
export interface AcademicSearchEricData {
  ericId: string;
  source?: string;
  publicationType?: string;
  educationLevel?: string[];
  audience?: string[];
  institution?: string;
  sponsor?: string;
  isbn?: string;
  issn?: string;
  descriptors?: string[];
  publisher?: string;
  peerReviewed?: boolean;
  fullTextAvailable?: boolean;
}

// Academic Search Union type for all provider data
export type AcademicSearchProviderData =
  | AcademicSearchSemanticScholarData
  | AcademicSearchGoogleScholarData
  | AcademicSearchPubMedData
  | AcademicSearchArxivData
  | AcademicSearchEricData;

// Academic Search Publication interface with optional provider data
export interface AcademicSearchPublication
  extends AcademicSearchBasePublication {
  providerData?: AcademicSearchProviderData;
}

// Academic Search Publication field type for field selection
export type AcademicSearchPublicationField =
  | "doi"
  | "title"
  | "url"
  | "providerURL"
  | "authors"
  | "date"
  | "year"
  | "totalCitations"
  | "totalReferences"
  | "abstract"
  | "pdfURL"
  | "provider"
  | "providerData";

export const AcademicSearchPublicationFieldValues: readonly AcademicSearchPublicationField[] =
  [
    "doi",
    "title",
    "url",
    "providerURL",
    "authors",
    "date",
    "year",
    "totalCitations",
    "totalReferences",
    "abstract",
    "pdfURL",
    "provider",
    "providerData",
  ] as const;

// Academic Search Provider error interface
export interface AcademicSearchProviderError {
  provider: string;
  message: string;
  code?: string;
  details?: Record<string, unknown>;
}

// Academic Search response interface
export interface AcademicSearchResponse {
  estimatedTotalResults: number;
  results: Partial<AcademicSearchPublication>[];
  errors?: AcademicSearchProviderError[];
}

// Academic Fetch result interface
export interface AcademicFetchResult extends AcademicSearchPublication {
  /** The ID that was used to fetch this publication */
  id: string;
  /** The provider that was detected/queried */
  detectedProvider: AcademicSearchProvider;
}

// Academic Fetch error interface
export interface AcademicFetchError {
  /** The ID that failed to fetch */
  id: string;
  /** Error message */
  error: string;
  /** Optional error code */
  code?: string;
}

// Academic Fetch response interface
export interface AcademicFetchResponse {
  /** Successfully fetched publications */
  results: AcademicFetchResult[];
  /** Errors for IDs that couldn't be fetched */
  errors?: AcademicFetchError[];
}

// Document Parse response interface
export interface ParseResponse {
  markdown: string;
  pageCount: number;
  creditCount: number;
  usedLLM: boolean;
}

// Temporary upload response interface
export interface TemporaryUploadResponse {
  key: string;
  url: string;
  exp: number;
}
